import { useState, useEffect } from "react";
import { useEtatGlobal } from "../EtatGlobal.js";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { authFetch } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog";
import {
  Building2,
  X,
  MessageSquare,
  CheckCircle2,
  Loader2,
  ClipboardCheck,
} from "lucide-react";

// ── Composant checkbox ligne ───────────────────────────────────────────────────
function CheckRow({ label, checked, onChange, updateDate }) {
  return (
    <label className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg hover:bg-white transition-colors cursor-pointer group">
      <div className="flex items-center gap-2.5">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={onChange}
          className="w-4 h-4 rounded border-gray-300 accent-[#840040] cursor-pointer shrink-0"
        />
        <span className="text-sm text-slate-700 group-hover:text-slate-900 transition-colors">
          {label}
        </span>
      </div>
      {updateDate && (
        <span className="text-[10px] text-slate-400 shrink-0">
          {new Date(updateDate).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      )}
    </label>
  );
}

// ── Section avec titre ─────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 space-y-1">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
        {title}
      </p>
      {children}
    </div>
  );
}

// ── PreparationPopup ───────────────────────────────────────────────────────────
function PreparationPopup({ selectedClient, setSelectedClient, fetchTodos }) {
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating] = useState(false);
  const [commentaireInput, setCommentaireInput] = useState("");
  const [commentaires, setCommentaires] = useState([]);
  const { utilisateur, patchTodoLocal } = useEtatGlobal();

  useEffect(() => {
    if (selectedClient) {
      setFormData({ ...selectedClient });
      setCommentaires(selectedClient.commentaires || []);
    }
  }, [selectedClient]);

  if (!selectedClient || selectedClient.type !== "Preparation") return null;

  // ── Sauvegarde immédiate ─────────────────────────────────────────────────────
  const saveDirectly = async (updatedFormData) => {
    const updatedFields = {};
    Object.keys(updatedFormData).forEach((key) => {
      if (updatedFormData[key] !== selectedClient[key]) {
        updatedFields[key] = updatedFormData[key];
      }
    });
    if (Object.keys(updatedFields).length === 0) return;
    try {
      await authFetch(
        `https://backend-myalfa.vercel.app/api/todos/${selectedClient.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedFields),
        }
      );
    } catch (error) {
      console.error("Erreur de sauvegarde auto :", error);
    }
  };

  // ── handleChange générique ────────────────────────────────────────────────────
  const handleChange = (field, value) => {
    const updateField = `${field}Update`;
    const newFormData = {
      ...formData,
      [field]: value,
      [updateField]:
        value === true ? new Date().toISOString() : formData[updateField],
    };
    setFormData(newFormData);
    saveDirectly(newFormData);
  };

  // ── Origine (reprise / creationActivite) : mise à jour combinée ───────────────
  const handleOrigine = (field, checked) => {
    const now = new Date().toISOString();
    const other = field === "reprise" ? "creationActivite" : "reprise";
    const otherUpdate = `${other}Update`;
    const newFormData = {
      ...formData,
      [field]: checked,
      [`${field}Update`]: checked ? now : formData[`${field}Update`],
      [other]: checked ? false : formData[other],
      [otherUpdate]: checked ? formData[otherUpdate] : formData[otherUpdate],
    };
    setFormData(newFormData);
    saveDirectly(newFormData);
  };

  // ── handleSave manuel ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    const updatedFields = {};
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== selectedClient[key]) {
        updatedFields[key] = formData[key];
      }
    });
    if (Object.keys(updatedFields).length === 0) return;

    // UI instantanée : la ligne reflète les champs modifiés tout de suite
    patchTodoLocal(selectedClient.id, updatedFields);

    setIsSaving(true);
    try {
      const response = await authFetch(
        `https://backend-myalfa.vercel.app/api/todos/${selectedClient.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedFields),
        }
      );
      if (!response.ok) throw new Error("Erreur lors de la mise à jour");
      fetchTodos();
    } catch (error) {
      console.error(error);
      fetchTodos(); // resynchronise l'état réel
      dialog.alert("Erreur serveur");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Commentaire ───────────────────────────────────────────────────────────────
  const handleAddComment = async () => {
    const texte = commentaireInput.trim();
    if (!texte) return;
    if (!utilisateur) return;

    const nomComplet = [utilisateur.lastName, utilisateur.name]
      .filter(Boolean)
      .join(" ") || utilisateur.name || "Anonyme";

    // Optimistic : ajout immédiat dans la liste
    const optimistic = {
      nomComplet,
      commentaire: texte,
      createdAt: new Date().toISOString(),
      _pending: true,
    };
    setCommentaires((prev) => [...prev, optimistic]);
    setCommentaireInput("");

    // Persistance en arrière-plan
    authFetch("https://backend-myalfa.vercel.app/api/commentairesTodo", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        nomComplet,
        commentaire: texte,
        todoId: selectedClient.id,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Erreur lors de l'ajout du commentaire");
        // Remplacer l'entrée optimiste par la vraie (sans _pending)
        setCommentaires((prev) =>
          prev.map((c) =>
            c._pending && c.commentaire === texte
              ? { ...c, _pending: false }
              : c
          )
        );
        fetchTodos();
      })
      .catch((err) => {
        console.error("Erreur en ajoutant le commentaire :", err);
        // Rollback : retirer l'entrée optimiste
        setCommentaires((prev) =>
          prev.filter((c) => !(c._pending && c.commentaire === texte))
        );
      });
  };

  // ── Validation finale ─────────────────────────────────────────────────────────
  const allChecked =
    formData.diaClient &&
    formData.extraitRCS &&
    formData.statusDPA &&
    formData.siren &&
    formData.pi &&
    formData.dbe &&
    formData.fiscal &&
    formData.lmSigner &&
    formData.rib &&
    formData.mandat &&
    (formData.reprise || formData.creationActivite) &&
    formData.lettreconfrere &&
    formData.kanta &&
    formData.gouv &&
    formData.ebics;

  const handleValidate = async () => {
    // UI instantanée : la tâche passe en "Terminer" et la popup se ferme tout
    // de suite ; sauvegarde, génération du document et mail continuent en
    // arrière-plan
    const todoId = selectedClient.id;
    patchTodoLocal(todoId, { status: "Terminer" });
    setSelectedClient(null);

    try {
      await handleSave();
      const pdfUrlGenerated = await generateWordDoc(formData);
      const mailResponse = await authFetch(
        "https://backend-myalfa.vercel.app/api/mail-event-complete",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: "DPA-0",
            clientName: selectedClient.nomClient,
            pdfurl: pdfUrlGenerated,
            userName: `${utilisateur.lastName} ${utilisateur.name}`,
            userMail: utilisateur.email,
          }),
        }
      );
      if (!mailResponse.ok) throw new Error("Erreur lors de l'envoi du mail");

      const response = await authFetch(
        `https://backend-myalfa.vercel.app/api/todos/${todoId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "Terminer" }),
        }
      );
      if (!response.ok)
        throw new Error("Erreur lors de la mise à jour du client");

      fetchTodos();
    } catch (error) {
      console.error("Erreur dans handleValidate :", error);
      fetchTodos(); // resynchronise l'état réel
      dialog.alert("Une erreur est survenue pendant la validation.");
    }
  };

  // ── Génération Word/PDF ───────────────────────────────────────────────────────
  async function loadTemplateFile(url) {
    const response = await fetch(url);
    return response.arrayBuffer();
  }

  const generateWordDoc = async (data) => {
    try {
      const content = await loadTemplateFile("/DPA-0.docx");
      const zip = new PizZip(content);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });

      const formatBool = (val) => (val ? "☒" : "☐");
      const formatDateTime = (isoString) => {
        if (!isoString) return "Non défini";
        const d = new Date(isoString);
        return `${d.toLocaleDateString("fr-FR")} ${d.toLocaleTimeString(
          "fr-FR",
          { hour: "2-digit", minute: "2-digit" }
        )}`;
      };
      const now = new Date();
      const currentDate = `${now.toLocaleDateString(
        "fr-FR"
      )} ${now.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;

      doc.render({
        nomClient: selectedClient.nomClient,
        diaClient: formatBool(data.diaClient),
        extraitRCS: formatBool(data.extraitRCS),
        statusDPA: formatBool(data.statusDPA),
        siren: formatBool(data.siren),
        pi: formatBool(data.pi),
        dbe: formatBool(data.dbe),
        fiscal: formatBool(data.fiscal),
        lmSigner: formatBool(data.lmSigner),
        rib: formatBool(data.rib),
        mandat: formatBool(data.mandat),
        reprise: formatBool(data.reprise),
        creationActivite: formatBool(data.creationActivite),
        lettreconfrere: formatBool(data.lettreconfrere),
        kanta: formatBool(data.kanta),
        dext: formatBool(data.dext),
        gouv: formatBool(data.gouv),
        ebics: formatBool(data.ebics),
        diaClientUpdate: data.diaClientUpdate
          ? formatDateTime(data.diaClientUpdate)
          : "",
        extraitRCSUpdate: data.extraitRCSUpdate
          ? formatDateTime(data.extraitRCSUpdate)
          : "",
        statusDPAUpdate: data.statusDPAUpdate
          ? formatDateTime(data.statusDPAUpdate)
          : "",
        sirenUpdate: data.sirenUpdate ? formatDateTime(data.sirenUpdate) : "",
        piUpdate: data.piUpdate ? formatDateTime(data.piUpdate) : "",
        dbeUpdate: data.dbeUpdate ? formatDateTime(data.dbeUpdate) : "",
        fiscalUpdate: data.fiscalUpdate
          ? formatDateTime(data.fiscalUpdate)
          : "",
        lmSignerUpdate: data.lmSignerUpdate
          ? formatDateTime(data.lmSignerUpdate)
          : "",
        ribUpdate: data.ribUpdate ? formatDateTime(data.ribUpdate) : "",
        mandatUpdate: data.mandatUpdate
          ? formatDateTime(data.mandatUpdate)
          : "",
        repriseUpdate: data.repriseUpdate
          ? formatDateTime(data.repriseUpdate)
          : "",
        creationActiviteUpdate: data.creationActiviteUpdate
          ? formatDateTime(data.creationActiviteUpdate)
          : "",
        lettreconfrereUpdate: data.lettreconfrereUpdate
          ? formatDateTime(data.lettreconfrereUpdate)
          : "",
        kantaUpdate: data.kantaUpdate ? formatDateTime(data.kantaUpdate) : "",
        dextUpdate: data.dextUpdate ? formatDateTime(data.dextUpdate) : "",
        gouvUpdate: data.gouvUpdate ? formatDateTime(data.gouvUpdate) : "",
        ebicsUpdate: data.ebicsUpdate ? formatDateTime(data.ebicsUpdate) : "",
        currentDate,
        creationDate: formatDateTime(selectedClient.createdAt),
        commentaires:
          selectedClient.commentaires?.map((c) => ({
            nomComplet: c.nomComplet || "Anonyme",
            commentaire: c.commentaire || "",
            createdAt: c.createdAt
              ? new Date(c.createdAt).toLocaleString("fr-FR")
              : "Date inconnue",
          })) || [],
      });

      const blob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const fileName = `DPA-0_client-${
        selectedClient.nomClient || "client"
      }.docx`;
      const file = new File([blob], fileName, {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const formDataToSend = new FormData();
      formDataToSend.append("file", file);

      const uploadResponse = await authFetch(
        "https://backend-myalfa.vercel.app/api/upload",
        { method: "POST", body: formDataToSend }
      );
      if (!uploadResponse.ok)
        throw new Error("Erreur lors de l'envoi du fichier");

      const { pdfUrl } = await uploadResponse.json();
      return pdfUrl;
    } catch (error) {
      console.error("Erreur de génération ou d'envoi :", error);
      dialog.alert("Une erreur est survenue lors du traitement.");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="overlayAC">
      <div className="inoverlayLead">
        <div className="bg-white rounded-xl w-[90%] mx-auto">
          {/* ── En-tête ─────────────────────────────────────────────────────── */}
          <div className="bg-white border-b-4 border-[#8B1538] px-6 pb-5 pt-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <img
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6969eee7337849d9e1d7fcb4/2957c578c_LogoAlfa-PNG-SansCartouche.png"
                  alt="Alfa"
                  className="h-14 object-contain"
                />
                <div className="h-10 w-px bg-slate-200" />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-slate-500" />
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      DPA-0 — Préparation dossier
                    </span>
                  </div>
                  <h1 className="text-xl font-bold text-slate-900 m-0">
                    {selectedClient.nomClient}
                  </h1>
                </div>
              </div>
              <button
                onClick={async () => {
                  await handleSave();
                  setSelectedClient(null);
                }}
                className="p-2 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
                aria-label="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* ── Corps ───────────────────────────────────────────────────────── */}
          <div className="px-6 py-5 space-y-4">
            {/* Bouton valider */}
            {allChecked && selectedClient.status !== "Terminer" && (
              <button
                onClick={handleValidate}
                disabled={isValidating}
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold py-3 rounded-xl transition shadow-sm"
              >
                {isValidating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Validation en
                    cours…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" /> Valider la tâche
                  </>
                )}
              </button>
            )}

            {/* Fiche DIA */}
            <Section title="Fiche client">
              <CheckRow
                label="Compléter la fiche DIA CLIENT"
                checked={formData.diaClient}
                onChange={(e) => handleChange("diaClient", e.target.checked)}
                updateDate={formData.diaClientUpdate}
              />
            </Section>

            {/* GED */}
            <Section title="Classement documents GED">
              <div className="grid grid-cols-2 gap-x-4">
                <CheckRow
                  label="Extrait R.C.S / R.M"
                  checked={formData.extraitRCS}
                  onChange={(e) => handleChange("extraitRCS", e.target.checked)}
                  updateDate={formData.extraitRCSUpdate}
                />
                <CheckRow
                  label="P.I"
                  checked={formData.pi}
                  onChange={(e) => handleChange("pi", e.target.checked)}
                  updateDate={formData.piUpdate}
                />
                <CheckRow
                  label="Status"
                  checked={formData.statusDPA}
                  onChange={(e) => handleChange("statusDPA", e.target.checked)}
                  updateDate={formData.statusDPAUpdate}
                />
                <CheckRow
                  label="Certificat dépôt DBE"
                  checked={formData.dbe}
                  onChange={(e) => handleChange("dbe", e.target.checked)}
                  updateDate={formData.dbeUpdate}
                />
                <CheckRow
                  label="Avis situation SIRENE"
                  checked={formData.siren}
                  onChange={(e) => handleChange("siren", e.target.checked)}
                  updateDate={formData.sirenUpdate}
                />
                <CheckRow
                  label="P/M0 / Mémento fiscal"
                  checked={formData.fiscal}
                  onChange={(e) => handleChange("fiscal", e.target.checked)}
                  updateDate={formData.fiscalUpdate}
                />
              </div>
            </Section>

            {/* Missions / GI */}
            <Section title="Missions / Mise en place G.I">
              <CheckRow
                label="Lettre(s) de missions signée(s)"
                checked={formData.lmSigner}
                onChange={(e) => handleChange("lmSigner", e.target.checked)}
                updateDate={formData.lmSignerUpdate}
              />
              <CheckRow
                label="RIB"
                checked={formData.rib}
                onChange={(e) => handleChange("rib", e.target.checked)}
                updateDate={formData.ribUpdate}
              />
              <CheckRow
                label="Mandat de prélèvement"
                checked={formData.mandat}
                onChange={(e) => handleChange("mandat", e.target.checked)}
                updateDate={formData.mandatUpdate}
              />

              {/* Origine : radio exclusif */}
              <div className="mt-2 pt-2 border-t border-slate-200">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                  Origine
                </p>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="origine"
                      checked={!!formData.reprise}
                      onChange={() => handleOrigine("reprise", true)}
                      className="w-4 h-4 accent-[#840040] cursor-pointer"
                    />
                    <span className="text-sm text-slate-700">
                      Reprise d'un confrère
                    </span>
                    {formData.repriseUpdate && (
                      <span className="text-[10px] text-slate-400">
                        {new Date(formData.repriseUpdate).toLocaleString(
                          "fr-FR",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </span>
                    )}
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="origine"
                      checked={!!formData.creationActivite}
                      onChange={() => handleOrigine("creationActivite", true)}
                      className="w-4 h-4 accent-[#840040] cursor-pointer"
                    />
                    <span className="text-sm text-slate-700">
                      Création d'activité
                    </span>
                    {formData.creationActiviteUpdate && (
                      <span className="text-[10px] text-slate-400">
                        {new Date(
                          formData.creationActiviteUpdate
                        ).toLocaleString("fr-FR", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    )}
                  </label>
                </div>
              </div>

              <CheckRow
                label="Lettre au confrère"
                checked={formData.lettreconfrere}
                onChange={(e) =>
                  handleChange("lettreconfrere", e.target.checked)
                }
                updateDate={formData.lettreconfrereUpdate}
              />
            </Section>

            {/* Autres spécificités */}
            <Section title="Autres spécificités">
              <div className="grid grid-cols-2 gap-x-4">
                <CheckRow
                  label="Création du dossier sous KANTA"
                  checked={formData.kanta}
                  onChange={(e) => handleChange("kanta", e.target.checked)}
                  updateDate={formData.kantaUpdate}
                />
                <CheckRow
                  label="Création de l'espace impôt.gouv"
                  checked={formData.gouv}
                  onChange={(e) => handleChange("gouv", e.target.checked)}
                  updateDate={formData.gouvUpdate}
                />
                <CheckRow
                  label="Création du compte DEXT"
                  checked={formData.dext}
                  onChange={(e) => handleChange("dext", e.target.checked)}
                  updateDate={formData.dextUpdate}
                />
                <CheckRow
                  label="Mise en place EBICS"
                  checked={formData.ebics}
                  onChange={(e) => handleChange("ebics", e.target.checked)}
                  updateDate={formData.ebicsUpdate}
                />
              </div>
            </Section>

            {/* Commentaires */}
            <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#840040]" />
                Commentaires
                {commentaires.length > 0 && (
                  <span className="ml-1 text-[10px] font-medium px-2 py-0.5 rounded-full text-white bg-[#840040]">
                    {commentaires.length}
                  </span>
                )}
              </h3>

              <div className="space-y-3 max-h-40 overflow-y-auto pr-1 mb-4">
                {commentaires.length > 0 ? (
                  commentaires.map((c, index) => (
                    <div
                      key={index}
                      className={`bg-white rounded-lg p-3 border border-[#84004030] transition-opacity ${
                        c._pending ? "opacity-60" : "opacity-100"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0 bg-[#840040]">
                          {(c.nomComplet || "A")[0]}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="font-semibold text-slate-800 text-sm">
                              {c.nomComplet || "Anonyme"}
                            </span>
                            {c.createdAt && (
                              <span className="text-[11px] text-[#840040]">
                                {new Date(c.createdAt).toLocaleString("fr-FR")}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 leading-relaxed">
                            {c.commentaire}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6 text-[#840040]">
                    <MessageSquare className="w-7 h-7 mx-auto mb-2 opacity-40" />
                    <p className="text-xs">Aucun commentaire pour le moment</p>
                  </div>
                )}
              </div>

              <div className="border-t border-[#84004030] pt-4">
                <textarea
                  value={commentaireInput}
                  onChange={(e) => setCommentaireInput(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  rows={2}
                  className="w-full text-sm rounded-lg p-3 border border-[#84004040] focus:outline-none focus:ring-2 focus:ring-[#84004040] resize-none"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={handleAddComment}
                    disabled={!commentaireInput.trim()}
                    className="text-white text-sm font-medium px-4 py-2 rounded-lg bg-[#840040] hover:bg-[#6c0034] transition disabled:opacity-50"
                  >
                    Envoyer
                  </button>
                </div>
              </div>
            </div>

            {/* Bouton enregistrer manuel (affiché si modif non sauvegardée) */}
            <div className="flex justify-end pb-4">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#840040] hover:bg-[#6c0034] disabled:opacity-60 text-white text-sm font-medium transition"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <ClipboardCheck className="w-3.5 h-3.5" /> Enregistrer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PreparationPopup;
